import { useProfileStore } from '@/features/profile/profileStore';
import { usePublicProfileStore } from '@/features/public/profile/profileStore';
import { createRouter, createWebHistory, type LocationQueryValue } from 'vue-router';
import { useModalStore, type ModalId } from '@/features/overlays/modals/modalStore';
import { storeToRefs } from 'pinia';
import { fetchMe, meQueryOptions } from '@/api/queries/me/useMeQuery';
import { customQueryClient } from '@/config/query-client';

import { useCompetencyStore } from '@/features/my-company/competency-framework/competencyStore';
import { MixpanelInstance, isMixpanelInitialized } from '@/plugins/mixpanel/mixpanel';

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      redirect: { name: 'profile' },
    },
    {
      path: '/public',
      name: 'public',
      meta: { group: 'public' },
      redirect: { name: 'not-found' },
      children: [
        {
          path: 'profile/:slug',
          name: 'public_profile',
          meta: { group: 'public' },
          component: () => import('@/views/UserPublicView.vue'),
          props: true,
          beforeEnter: (to) => {
            const { profileSlug } = storeToRefs(usePublicProfileStore());
            profileSlug.value = to.params.slug as string;
          },
        },
      ],
    },
    {
      path: '/profile/pole-emploi',
      redirect: '/profile/france-travail',
    },
    {
      path: '/profile',
      name: 'profile',
      component: () => import('@/views/ProfileView.vue'),
      redirect: { name: 'profile_synthesis' },
      children: [
        {
          path: 'synthesis',
          name: 'profile_synthesis',
          component: () => import('@/features/profile/synthesis/SynthesisView.vue'),
        },
        {
          path: 'career-path',
          name: 'profile_career',
          component: () => import('@/features/profile/career/CareerView.vue'),
          beforeEnter: async () => {
            const me = await customQueryClient.ensureQueryData({ queryKey: meQueryOptions.queryKey, queryFn: fetchMe });
            if (!me?.permissions?.includes('capa')) {
              return { name: 'profile_synthesis' };
            }
            return true;
          },
        },
        {
          path: 'france-travail',
          name: 'profile_pole-emploi',
          component: () => import('@/features/profile/pole-emploi/PoleEmploiView.vue'),
          beforeEnter: async () => {
            const me = await customQueryClient.ensureQueryData({ queryKey: meQueryOptions.queryKey, queryFn: fetchMe });
            if (!me?.is_pole_emploi) {
              return { name: 'profile_synthesis' };
            }
            return true;
          },
        },
      ],
    },
    {
      path: '/user/:uuid',
      name: 'user-view',
      component: () => import('@/views/UserView.vue'),
      props: true,
      beforeEnter: (to) => {
        const { profileUuid } = storeToRefs(useProfileStore());
        profileUuid.value = to.params.uuid as string;
      },
    },
    {
      path: '/network',
      name: 'network',
      component: () => import('@/views/NetworkView.vue'),
      redirect: { name: 'network_invitations' },
      children: [
        {
          path: 'invitations',
          name: 'network_invitations',
          component: () => import('@/features/network/invitations/InvitationsView.vue'),
        },
        {
          path: 'contacts',
          name: 'network_contacts',
          component: () => import('@/features/network/contacts/ContactsView.vue'),
        },
        {
          path: 'companies',
          name: 'network_companies',
          component: () => import('@/features/network/companies/CompaniesView.vue'),
        },
      ],
    },
    {
      path: '/jobs',
      name: 'jobs',
      component: () => import('@/views/SourcingView.vue'),
      redirect: { name: 'jobs_invitations' },
      beforeEnter: async () => {
        const me = await customQueryClient.ensureQueryData({ queryKey: meQueryOptions.queryKey, queryFn: fetchMe });
        if (!me?.permissions?.includes('sc_pa')) {
          return { name: 'profile' };
        }
        return true;
      },
      children: [
        {
          path: 'invitations/:uuid?',
          name: 'jobs_invitations',
          component: () => import('@/features/sourcing/InvitationsView.vue'),
        },
      ],
    },
    {
      path: '/my-company',
      name: 'my-company',
      component: () => import('@/views/MyCompanyView.vue'),
      redirect: { name: 'competency-framework' },
      children: [
        {
          path: 'competency-framework/:uuid?',
          name: 'competency-framework',
          component: () =>
            import('@/features/my-company/competency-framework/CompetencyFrameworkTab.vue'),
        },
        {
          path: 'development-plans',
          name: 'development-plans',
          component: () => import('@/features/my-company/development-plan/DevelopmentPlanTab.vue'),
        },
      ],
    },
    {
      path: '/development-plan',
      component: () => import('@/views/DevelopmentPlanView.vue'),
      children: [
        {
          path: 'create',
          name: 'dev-plan-create',
          component: () =>
            import('@/features/my-company/development-plan/create/CreateDevPlan.vue'),
          beforeEnter: (to, from, next) => {
            const { selectedUuid } = storeToRefs(useCompetencyStore());

            // Redirects to profile view in case CF is not selected
            if (!selectedUuid.value) next({ name: 'profile' });
            else next();
          },
        },
        {
          path: ':uuid',
          name: 'dev-plan',
          redirect: { name: 'dev-plan-detail' },
          children: [
            {
              path: ':actionPlan?',
              name: 'dev-plan-detail',
              component: () =>
                import('@/features/my-company/development-plan/action-plan/ActionPlanHome.vue'),
            },
            {
              path: 'action-plan/:actionUuid',
              name: 'action-plan',
              children: [
                {
                  path: 'intro',
                  name: 'introduction',
                  component: () =>
                    import('@/features/my-company/development-plan/action-plan/ActionPlanIntro.vue'),
                },
                {
                  path: 'habit/:number',
                  name: 'habit',
                  component: () =>
                    import('@/features/my-company/development-plan/action-plan/Habit.vue'),
                },
                {
                  path: 'further',
                  name: 'further',
                  component: () =>
                    import('@/features/my-company/development-plan/action-plan/ActionPlanFurther.vue'),
                },
                {
                  path: 'congrats',
                  name: 'congrats',
                  component: () =>
                    import('@/features/my-company/development-plan/action-plan/ActionPlanCongrats.vue'),
                },
              ],
            },
          ],
        },
      ],
    },

    {
      path: '/settings',
      name: 'settings',
      redirect: { name: 'settings_personal-info' },
      component: () => import('@/views/SettingsView.vue'),
      children: [
        {
          path: 'personal-info',
          name: 'settings_personal-info',
          component: () => import('@/features/settings/personal-info/PersonalInfoView.vue'),
        },
        {
          path: 'login',
          name: 'settings_login',
          component: () => import('@/features/settings/login/LoginView.vue'),
        },
        {
          path: 'privacy',
          name: 'settings_privacy',
          component: () => import('@/features/settings/privacy/PrivacyView.vue'),
        },
      ],
    },
    {
      path: '/404',
      name: 'not-found',
      component: () => import('@/views/NotFoundView.vue'),
    },
    {
      path: '/403',
      name: 'forbidden',
      component: () => import('@/views/ForbiddenView.vue'),
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: { name: 'not-found' },
    },
  ],
  // Anchors
  scrollBehavior(to) {
    // Page must be fully mounted before triggering scroll
    if (to.hash && document.querySelector(to.hash)) {
      return {
        el: to.hash,
        behavior: 'smooth',
        top: 70,
      };
    }
  },
});

const cfRoutes = ['development-plans', 'dev-plan-create', 'dev-plan-detail'];

router.beforeEach(async (to) => {
  // Check for modal
  checkForModal(to);

  // redirect if user is mobile on these specific routes
  // provisoir solution, waiting for a mobile version
  if (to.name && cfRoutes.includes(to.name.toString()) && window.innerWidth < 1024) {
    return { name: 'profile' };
  }
});

// After each redirection of each page
router.afterEach((to, _from) => {
  if (isMixpanelInitialized()) {
    // end time reporter
    if (_from.name) {
      MixpanelInstance.track(`Leave ${_from.name.toString()}`);
    }

    // start new time reporter, and enter log
    // First load in App.vue
    if (to.name && _from.name) {
      MixpanelInstance.time_event(`Leave ${to.name.toString()}`);
      MixpanelInstance.track(`Enter ${to.name.toString()}`, {
        entry_point: _from.name?.toString(),
      });
    }
  }
});

//-- Usefull
// @todo: create helper

/**
 * Check for modal query parameter
 * @param to
 */
const checkForModal = (to) => {
  const store = useModalStore();
  const modalIds = Object.keys(store.$state);

  function validateParameterType(param: LocationQueryValue) {
    if (param && modalIds.includes(param)) return param as ModalId;
    else return undefined;
  }

  const showModal = validateParameterType(to.query?.showModal as LocationQueryValue);

  if (showModal) store.toggle(showModal);
};

export default router;
